import React, { ReactElement, ReactNode, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";

import Button from "components/ui/Button";
import Text from "components/ui/Text";

import "./ToastProvider.scss";
import styles from "./ToastContext.module.scss";

export type ToastContextType = {
  showToast: ShowToastFunc;
  showLoadingToast: (message: string) => ToastHandle;
  showActionToast: (message: ReactNode, action: ToastAction) => void;
};

/** A button in the toast; clicking it also closes the toast. */
export type ToastAction = {
  label: string;
  onClick: () => void;
};

export type ShowToastFunc = (message: string, variant?: ToastVariant) => void;

/** Handle to a persistent loading toast; call success/error to resolve it in place. */
export type ToastHandle = {
  success: (message: string) => void;
  error: (message: string) => void;
  dismiss: () => void;
};

type ToastVariant = "success" | "error" | "warning" | "default";

const ToastContext = React.createContext<ToastContextType | null>(null);
ToastContext.displayName = "ToastContext";

export function useToast() {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

type ProviderProps = {
  children: ReactNode;
};

export function ToastProvider({ children }: ProviderProps) {
  const showToast = useCallback((message: string, variant: ToastVariant = "default") => {
    toast(<Text variant="body-2">{message}</Text>, {
      type: variant,
      icon: icons[variant],
    });
  }, []);

  const showLoadingToast = useCallback((message: string): ToastHandle => {
    const id = toast.loading(<Text variant="body-2">{message}</Text>);
    const resolve = (msg: string, variant: "success" | "error") =>
      toast.update(id, {
        render: <Text variant="body-2">{msg}</Text>,
        type: variant,
        icon: icons[variant],
        isLoading: false,
        autoClose: 5000,
      });
    return {
      success: msg => resolve(msg, "success"),
      error: msg => resolve(msg, "error"),
      dismiss: () => toast.dismiss(id),
    };
  }, []);

  const showActionToast = useCallback((message: ReactNode, action: ToastAction) => {
    toast(
      <div className={styles.withAction}>
        <Text variant="body-2">{message}</Text>
        <Button variant="secondary" size="small" onClick={action.onClick}>
          {action.label}
        </Button>
      </div>,
      { type: "default", icon: icons.default }
    );
  }, []);

  const value = React.useMemo(
    () => ({ showToast, showLoadingToast, showActionToast }),
    [showToast, showLoadingToast, showActionToast]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}

      <ToastContainer
        position="bottom-center"
        autoClose={5000}
        hideProgressBar
        closeOnClick
        // No pauseOnFocusLoss: it froze the dismiss timer whenever the tab
        // blurred, leaving "transient" toasts parked indefinitely for anyone
        // who switches windows. Hover still pauses (deliberate reading aid).
        pauseOnHover
      />
    </ToastContext.Provider>
  );
}

// Functions, not elements: react-toastify clones an element icon with its own props, including a
// `theme` that MUI's styled engine then tries to write back onto the frozen props. Passing a
// function keeps those props off the icon, which receives only what is set here.
const icons: Record<ToastVariant, () => ReactElement> = {
  success: () => (
    <CheckCircleIcon fontSize="large" htmlColor="var(--status-success-fg)" />
  ), // --status-success-fg
  error: () => <ErrorIcon fontSize="large" htmlColor="var(--status-danger-fg)" />, // --status-danger-fg
  warning: () => <WarningIcon fontSize="large" htmlColor="var(--status-warning-fg)" />, // --status-warning-fg
  default: () => <InfoIcon fontSize="large" htmlColor="var(--text-heading)" />, // --text-heading
};
